/**
 * Responsibility: describe the mesa country biome — flat-topped tables of rock with cliff sides.
 *
 * ## Quantised, not smooth
 *
 * Every other terrain biome is fBm, which is smooth by construction. A mesa is not: it has a flat top and a
 * cliff, and no tuning of a smooth field produces either. So the field is sampled smoothly and then **snapped
 * to discrete levels** — a plateau is everywhere the field falls in one band, and a cliff is a band boundary.
 *
 * One noise sample and a floor division, which makes this among the cheapest biomes in the world despite being
 * the most structurally distinct one in it.
 *
 * ## Buttes come free
 *
 * A butte is an eroded remnant of a mesa, and that is what quantising gives where the field only just crosses a
 * threshold: a small isolated patch at the higher level. Broad crossings give wide mesas, narrow ones give
 * buttes. Nothing distinguishes them in code.
 *
 * ## Why the floor is not flat
 *
 * A pure staircase leaves the desert floor a mathematically flat plane — the "large uniform expanse" the
 * steering file forbids, and a surface with no distance cue at all in wireframe. A low-amplitude smooth field is
 * added on top of the terraces to fix that. It also means mesa tops undulate slightly, which real ones do.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { createFbm, toUnitRange } from '../noise.js';
import { createRockFormations } from '../rockFormations.js';
import { MESA, PALETTE } from '../../config.js';

export const mesa = {
  id: 'mesa',
  label: 'Mesa country',

  palette: PALETTE.mesa,

  suitabilityBias: MESA.SUITABILITY_BIAS,

  /**
   * Build the elevation function for this biome.
   *
   * @param {number} seed - Base world seed.
   * @returns {(x: number, z: number) => number} Elevation in world units, always >= 0.
   */
  createElevation(seed) {
    const plateau = createFbm(seed, 'elevation:mesa', {
      octaves: MESA.OCTAVES,
      baseFrequency: 1 / MESA.WAVELENGTH,
      persistence: MESA.PERSISTENCE,
      lacunarity: MESA.LACUNARITY,
    });

    /*
      A separate field for the floor undulation, so it is statistically independent of where the plateaus are.
      Sharing one would make the floor rise exactly where the terraces do and the two would read as one feature.
    */
    const relief = createFbm(seed, 'relief:mesa', {
      octaves: 2,
      baseFrequency: 1 / MESA.FLOOR_WAVELENGTH,
      persistence: 0.4,
      lacunarity: MESA.LACUNARITY,
    });

    /** Highest terrace index, so the top level is reachable but never overshoots. */
    const topLevel = Math.max(1, MESA.LEVELS - 1);

    return function sampleMesaElevation(x, z) {
      /*
        Remapped to 0..1 so this biome can never contribute a negative elevation. Blended elevation is a weighted
        sum, so a negative contribution would pull neighbouring terrain down through zero rather than flattening it.
      */
      const unit = toUnitRange(plateau(x, z));

      /*
        Stretch the band the field *actually* occupies across the terraces.

        fBm never approaches its own extremes — this one runs 0.20 to 0.80 and clusters around the middle — so
        quantising the raw value put the top terrace above a height the noise never produces and that terrace
        simply did not exist. The forest hit the same wall with its stand field. See MESA.FIELD_LOW.
      */
      const stretched = Math.min(
        1,
        Math.max(0, (unit - MESA.FIELD_LOW) / (MESA.FIELD_HIGH - MESA.FIELD_LOW)),
      );

      /*
        `RARITY` above 1 pushes most of the field into the lowest band, so the desert floor dominates and the
        upper tiers are isolated. That is what makes a mesa a landmark rather than one step of a staircase.
      */
      const shaped = Math.pow(stretched, MESA.RARITY);

      /*
        The quantisation itself, and the whole biome. `min` guards the single input value that would otherwise
        land one level above the top.
      */
      const level = Math.min(topLevel, Math.floor(shaped * MESA.LEVELS));

      return level * MESA.STEP_HEIGHT + toUnitRange(relief(x, z)) * MESA.FLOOR_RELIEF;
    };
  },

  /** Highest elevation this biome can produce, in world units. */
  maxElevation: Math.max(1, MESA.LEVELS - 1) * MESA.STEP_HEIGHT + MESA.FLOOR_RELIEF,

  /**
   * Build the structure placement function: arches, spires and hoodoos standing on the terraces.
   *
   * Shared with canyon country, which is the same geology. None of these can be terrain — the lattice is too
   * coarse for a spire, a hoodoo's caprock is an overhang, and an arch is a hole. See `world/rockFormations.js`.
   *
   * @param {number} seed - Base world seed.
   * @returns {(region: object, context: object) => Array<object>}
   */
  createStructures(seed) {
    return createRockFormations(seed, 'mesa');
  },
};
